/**
 * Billing Dashboard — 8 KPIs + 5 charts on one screen.
 * Reads from GET /billing/dashboard.
 */
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  TrendingUp, TrendingDown, Wallet, Receipt, AlertCircle, RotateCcw,
  Calendar as CalendarIcon, DollarSign, Loader2,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip,
  AreaChart, Area, PieChart, Pie, Cell, CartesianGrid, Legend,
} from "recharts";
import { Link } from "react-router-dom";
import BillingTabs from "@/components/app/BillingTabs";

const INR = (n) => `₹${Number(n || 0).toLocaleString("en-IN", {
  minimumFractionDigits: 0, maximumFractionDigits: 0,
})}`;
const INR_SHORT = (n) => {
  const v = Number(n || 0);
  if (v >= 1e7) return `₹${(v / 1e7).toFixed(1)}Cr`;
  if (v >= 1e5) return `₹${(v / 1e5).toFixed(1)}L`;
  if (v >= 1e3) return `₹${(v / 1e3).toFixed(1)}k`;
  return `₹${v}`;
};

const PIE_COLORS = ["#0F4C3A", "#5A8A6E", "#D06A42", "#7C5FA6", "#3A7EA1", "#B94A48", "#8A9A5B"];

export default function BillingDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/billing/dashboard");
      setData(data);
    } catch { toast.error("Could not load dashboard"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading || !data) {
    return (
      <div>
        <BillingTabs />
        <div className="py-16 text-center text-ink-soft"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
      </div>
    );
  }

  const k = data.kpis || {};

  return (
    <div data-testid="billing-dashboard">
      <BillingTabs />
      <div className="mb-6">
        <div className="overline text-ink-soft">Billing · Dashboard</div>
        <h1 className="mt-1 font-display text-4xl tracking-tight text-ink">
          The books, quietly balanced.
        </h1>
        <div className="text-sm text-ink-mid mt-1">
          Live financial pulse for the last 30 days · everything reads from the ledger.
        </div>
      </div>

      {/* 8 KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <KPI label="Today's collection"    value={INR(k.today_collection)}
             icon={DollarSign} tone="green" testid="dash-kpi-today" />
        <KPI label="This month"            value={INR(k.month_collection)}
             icon={TrendingUp} tone="pine" testid="dash-kpi-month" />
        <KPI label="Outstanding"           value={INR(k.outstanding)}
             icon={Receipt} tone="amber" testid="dash-kpi-outstanding"
             linkTo="/app/billing/outstanding" />
        <KPI label="Wallet balance"        value={INR(k.wallet_total)}
             icon={Wallet} tone="purple" testid="dash-kpi-wallet" />
        <KPI label="Pending invoices"      value={k.pending_invoices}
             icon={CalendarIcon} tone="muted" testid="dash-kpi-pending" />
        <KPI label="Overdue (>90d)"        value={INR(k.overdue_90plus)}
             icon={AlertCircle} tone="red" testid="dash-kpi-overdue" />
        <KPI label="Refunds this month"    value={INR(k.refunds_month)}
             icon={RotateCcw} tone="muted" testid="dash-kpi-refunds" />
        <KPI label="Avg / day"             value={INR(k.avg_per_day)}
             icon={TrendingDown} tone="pine" testid="dash-kpi-avg" />
      </div>

      {/* Charts row 1: Trend + Method distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        <Panel title="Collection trend — last 30 days" className="lg:col-span-2">
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={data.collection_trend} margin={{ left: 0, right: 8, top: 8, bottom: 0 }}>
              <defs>
                <linearGradient id="trend" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%"  stopColor="#0F4C3A" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#0F4C3A" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#7A8880" }}
                     tickFormatter={(d) => d.slice(5)} />
              <YAxis tick={{ fontSize: 10, fill: "#7A8880" }}
                     tickFormatter={INR_SHORT} />
              <CartesianGrid strokeDasharray="3 3" stroke="#EEE" />
              <Tooltip formatter={(v) => INR(v)} labelStyle={{ fontSize: 12 }}
                       contentStyle={{ borderRadius: 12, border: "1px solid #E8EAE9" }} />
              <Area type="monotone" dataKey="amount" stroke="#0F4C3A"
                    strokeWidth={2} fill="url(#trend)" />
            </AreaChart>
          </ResponsiveContainer>
        </Panel>

        <Panel title="Payment method mix">
          {data.payment_methods.length === 0 ? (
            <div className="text-sm text-ink-soft text-center py-10">No payments yet</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={data.payment_methods} dataKey="amount" nameKey="method"
                     cx="50%" cy="50%" innerRadius={45} outerRadius={80}
                     stroke="none" paddingAngle={2}>
                  {data.payment_methods.map((_, i) => (
                    <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(v) => INR(v)} contentStyle={{ borderRadius: 12 }} />
                <Legend iconType="circle" wrapperStyle={{ fontSize: 10 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Panel>
      </div>

      {/* Charts row 2: Aging + Top treatments + Doctors */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        <Panel title="Outstanding aging">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data.aging} margin={{ left: 0, right: 8, top: 8, bottom: 0 }}>
              <XAxis dataKey="bucket" tick={{ fontSize: 10, fill: "#7A8880" }} />
              <YAxis tick={{ fontSize: 10, fill: "#7A8880" }} tickFormatter={INR_SHORT} />
              <CartesianGrid strokeDasharray="3 3" stroke="#EEE" />
              <Tooltip formatter={(v) => INR(v)} contentStyle={{ borderRadius: 12 }} />
              <Bar dataKey="amount" radius={[8, 8, 0, 0]}>
                {data.aging.map((_, i) => (
                  <Cell key={i} fill={["#84CC94","#F6C46B","#F09256","#EF4444","#B4285D","#7C3AED"][i % 6]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Panel>

        <Panel title="Top revenue treatments">
          {data.top_treatments.length === 0 ? (
            <div className="text-sm text-ink-soft text-center py-10">Not enough data yet</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={data.top_treatments} layout="vertical"
                        margin={{ left: 8, right: 8, top: 4, bottom: 0 }}>
                <XAxis type="number" tickFormatter={INR_SHORT}
                       tick={{ fontSize: 10, fill: "#7A8880" }} />
                <YAxis type="category" dataKey="name" width={120}
                       tick={{ fontSize: 10, fill: "#7A8880" }} />
                <Tooltip formatter={(v) => INR(v)} contentStyle={{ borderRadius: 12 }} />
                <Bar dataKey="amount" fill="#5A8A6E" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Panel>

        <Panel title="Revenue by doctor (30d)">
          {data.revenue_by_doctor.length === 0 ? (
            <div className="text-sm text-ink-soft text-center py-10">No completed treatments</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={data.revenue_by_doctor}
                        margin={{ left: 0, right: 8, top: 8, bottom: 0 }}>
                <XAxis dataKey="doctor_name" tick={{ fontSize: 10, fill: "#7A8880" }} />
                <YAxis tick={{ fontSize: 10, fill: "#7A8880" }} tickFormatter={INR_SHORT} />
                <Tooltip formatter={(v) => INR(v)} contentStyle={{ borderRadius: 12 }} />
                <Bar dataKey="amount" fill="#0F4C3A" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Panel>
      </div>

      {/* Quick actions */}
      <div className="rounded-2xl border border-[#E8EAE9] bg-white p-4">
        <div className="text-[10px] uppercase tracking-widest text-ink-soft mb-3">Quick actions</div>
        <div className="flex flex-wrap gap-2">
          <Link to="/app/billing/invoices" className="btn-primary text-xs px-4 py-2 rounded-full">+ New invoice</Link>
          <Link to="/app/billing/outstanding" className="text-xs px-4 py-2 rounded-full border border-pine/20 hover:bg-linen">
            View outstanding
          </Link>
          <Link to="/app/billing/reports" data-testid="dash-goto-reports"
                className="text-xs px-4 py-2 rounded-full border border-pine/20 hover:bg-linen">
            Reports & exports
          </Link>
        </div>
      </div>
    </div>
  );
}


function KPI({ label, value, icon: Icon, tone = "pine", testid, linkTo }) {
  const tones = {
    pine:   "bg-white border-[#E8EAE9] text-ink",
    green:  "bg-emerald-50 border-emerald-200 text-emerald-800",
    amber:  "bg-amber-50 border-amber-200 text-amber-900",
    red:    "bg-red-50 border-red-200 text-red-800",
    purple: "bg-purple-50 border-purple-200 text-purple-800",
    muted:  "bg-linen border-[#E8EAE9] text-ink-mid",
  };
  const Card = (
    <div className={`rounded-2xl border px-4 py-3 h-full ${tones[tone]} ${linkTo ? "hover:shadow-md cursor-pointer transition" : ""}`}
         data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest opacity-70 flex items-center gap-1">
        {Icon && <Icon className="w-3 h-3" />} {label}
      </div>
      <div className="font-mono text-2xl mt-1">{value}</div>
    </div>
  );
  return linkTo ? <Link to={linkTo}>{Card}</Link> : Card;
}


function Panel({ title, children, className = "" }) {
  return (
    <div className={`rounded-2xl border border-[#E8EAE9] bg-white p-4 ${className}`}>
      <div className="text-[10px] uppercase tracking-widest text-ink-soft mb-2">{title}</div>
      {children}
    </div>
  );
}
